import { useCallback, useState, useEffect } from 'react';
import { ElementWithUserNames, ElementType, ElementTypeLabels, ElementInfo } from '../../../../types/element.types';
import { useElementUpdates } from '../../../../hooks/dataUpdates/useElementUpdates';

// 定义包含数量信息的 clipboard 项类型
type ClipboardItem = ElementInfo & { elementQtyInScene?: number };

// 全局粘贴板（跨组件共享）
// 存储的数据包含 elementQtyInScene 字段（来自 ElementWithUserNames）
const clipboard: Map<ElementType, ClipboardItem[]> = new Map();

// 用于通知 clipboard 变化的回调函数列表
const clipboardChangeListeners: Set<() => void> = new Set();

/**
 * 通知所有监听者 clipboard 已变化
 */
const notifyClipboardChange = (): void => {
  clipboardChangeListeners.forEach(listener => listener());
};

/**
 * 添加 clipboard 变化监听器
 */
export const addClipboardChangeListener = (listener: () => void): void => {
  clipboardChangeListeners.add(listener);
};

/**
 * 移除 clipboard 变化监听器
 */
export const removeClipboardChangeListener = (listener: () => void): void => {
  clipboardChangeListeners.delete(listener);
};

/**
 * 获取待粘贴板中所有元素的总数
 */
export const getTotalClipboardCount = (): number => {
  let total = 0;
  for (const [, items] of Array.from(clipboard.entries())) {
    total += items.length;
  }
  return total;
};

/**
 * 获取粘贴板实例（用于其他 Hook 访问）
 */
export const getClipboard = (): Map<ElementType, ClipboardItem[]> => {
  return clipboard;
};

/**
 * 设置粘贴板内容（带通知）
 */
export const setClipboard = (category: ElementType, items: ClipboardItem[]): void => {
  clipboard.set(category, items);
  notifyClipboardChange();
};

/**
 * 清空粘贴板
 */
export const clearClipboard = (): void => {
  clipboard.clear();
  notifyClipboardChange();
};

/**
 * 将当前场次全部分类的元素关系写入待粘贴板（与表格内逐分类「复制该分类所有元素」一致的数据结构，一次性覆盖整张粘贴板）。
 */
export const setClipboardFromFullScene = (elements: ElementWithUserNames[]): void => {
  clipboard.clear();
  const byType = new Map<ElementType, ClipboardItem[]>();
  for (const item of elements) {
    const cat = item.elementType as ElementType;
    const arr = byType.get(cat);
    if (arr) {
      arr.push(item as ClipboardItem);
    } else {
      byType.set(cat, [item as ClipboardItem]);
    }
  }
  for (const [cat, items] of Array.from(byType.entries())) {
    clipboard.set(cat, items);
  }
  notifyClipboardChange();
};

/**
 * 批量粘贴场次元素 Hook
 * 
 * 功能：
 * - 批量粘贴所有分类的元素到当前场次
 * - 排重检查（跳过已存在的元素）
 * - 数据验证
 * - 分批处理（每批最多100个）
 * - 错误处理（400错误时逐个创建）
 * 
 * @returns {success: boolean, error?: string} 粘贴结果
 */
export const useBatchPasteElements = (
  elements: ElementWithUserNames[],
  sceneId: string,
  breakdownId?: string,
  onElementRemoved?: () => void
) => {
  const { batchCreateElementRelationsData, createElementRelationData } = useElementUpdates();
  
  // 响应式的 clipboard count state
  const [clipboardCount, setClipboardCount] = useState<number>(getTotalClipboardCount());
  
  // 监听 clipboard 变化
  useEffect(() => {
    const refreshClipboardCount = () => {
      setClipboardCount(getTotalClipboardCount());
    };
    
    // 初始化时设置一次
    refreshClipboardCount();
    
    // 添加监听器
    addClipboardChangeListener(refreshClipboardCount);
    
    // 清理监听器
    return () => {
      removeClipboardChangeListener(refreshClipboardCount);
    };
  }, []);
  
  const handleBatchPaste = useCallback(async (): Promise<{success: boolean, error?: string}> => {
    const totalClipboardCount = getTotalClipboardCount();
    if (totalClipboardCount === 0) {
      return { success: false, error: '待粘贴板中没有可粘贴的元素' };
    }
    
    // 批量粘贴所有分类的元素
    let addedCount = 0;
    for (const [category, items] of Array.from(clipboard.entries())) {
      if (items.length === 0) continue;
      
      try {
        // 获取当前场次已有的该分类的元素ID列表，用于排重
        const existingItemIds = new Set(
          elements
            .filter(item => item.elementType === category)
            .map(item => item.elementID)
        );
        
        // 过滤出需要新增的元素（不在当前场次中的）
        const itemsToAdd = items.filter((item) => !existingItemIds.has(item.elementID));
        
        if (itemsToAdd.length === 0) continue;
        
        // 批量创建场次元素关系（使用批量API，分批次处理100个限制）
        // 复制时包含的数量信息会被一起复制，粘贴时使用该数量创建关系
        const relations = itemsToAdd.map((item: ClipboardItem) => ({
          elementID: item.elementID,
          sceneID: sceneId,
          elementQtyInScene: item.elementQtyInScene, // 使用复制时的数量
        }));
        
        // 验证数据完整性
        const validRelations = relations.filter(relation => {
          // 检查必填字段
          if (!relation.elementID) {
            return false;
          }
          
          // 检查sceneID是否有效
          if (!relation.sceneID) {
            return false;
          }
          
          return true;
        });
        
        if (validRelations.length === 0) {
          continue;
        }
        
        if (!breakdownId) {
          console.error('缺少 breakdownId，无法创建关系');
          continue;
        }
        
        // 分批处理，每批最多100个关系
        const BATCH_SIZE = 100;
        
        // 如果关系数量小于等于批处理大小，直接发送并检查错误
        if (validRelations.length <= BATCH_SIZE) {
          try {
            await batchCreateElementRelationsData(breakdownId, validRelations);
          } catch (error: any) {
            // 如果是400错误，可能是关系已存在或后端有其他限制
            if (error.response?.status === 400) {
              // 尝试逐个创建关系，跳过已存在的
              for (const relation of validRelations) {
                try {
                  await createElementRelationData(
                    breakdownId,
                    relation.elementID,
                    relation.sceneID,
                    {
                      elementQtyInScene: relation.elementQtyInScene, // 使用复制时的数量
                    }
                  );
                } catch (singleError: any) {
                  // 检查是否是"关系已存在"的错误（可能是409或400状态码）
                  const errorMessage = singleError?.response?.data?.message || singleError?.message || '';
                  const isRelationExistsError = singleError.response?.status === 409 || 
                                                (singleError.response?.status === 400 && 
                                                 (errorMessage.includes('关系已存在') || errorMessage.includes('已存在')));
                  
                  if (!isRelationExistsError) {
                    throw singleError; // 其他错误直接抛出
                  }
                  // 关系已存在，继续下一个
                }
              }
            } else {
              throw error; // 其他错误直接抛出
            }
          }
        } else {
          // 大数据量使用分批处理
          for (let i = 0; i < validRelations.length; i += BATCH_SIZE) {
            const batch = validRelations.slice(i, i + BATCH_SIZE);
            await batchCreateElementRelationsData(breakdownId, batch);
          }
        }

        addedCount += itemsToAdd.length;
      } catch (error: any) {
        console.error(`粘贴${ElementTypeLabels[category]}元素失败:`, error);
        const errorMessage = error?.response?.data?.message || error?.message || '未知错误';
        return { success: false, error: `粘贴${ElementTypeLabels[category]}元素失败: ${errorMessage}` };
      }
    }
    
    if (addedCount > 0) {
      // 通知父组件更新已选择元素列表
      onElementRemoved?.();
      // 清空粘贴板
      clearClipboard();
      // 成功粘贴，不显示提示对话框（避免 Windows 系统对话框导致 APP 失焦）
      console.log(`成功添加 ${addedCount} 个元素到当前场次`);
      return { success: true };
    } else {
      // 所有元素已存在，不显示提示（静默处理）
      console.log('所有可粘贴的元素已存在于当前场次');
      return { success: true };
    }
  }, [elements, sceneId, breakdownId, onElementRemoved, batchCreateElementRelationsData, createElementRelationData]);

  return {
    handleBatchPaste,
    getTotalClipboardCount,
    clipboardCount, // 返回响应式的 clipboard count
  };
};

